/**
 * Auth Callback Page — KC Authorization Code Exchange
 *
 * Receives the ?code= redirect from Keycloak after:
 *   - Native magic link click (execute-actions-email action token)
 *   - Standard PKCE login redirect
 *
 * Flow:
 *   1. KC action token resolves → KC redirects here with ?code=
 *   2. This page posts { code, redirect_uri, code_verifier? } to the API
 *   3. API calls KC /token (public client, no secret) → returns full JWT
 *   4. Tokens are stored and user is navigated to dashboard
 *
 * Note: PKCE is optional on guardrail-web — magic link flow has no code_challenge;
 * standard login flow stores code_verifier in sessionStorage keyed by state.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { PKCE_SESSION_KEY_PREFIX } from '@/lib/pkce';
import { OAUTH_PENDING_KEY } from './OAuthAuthorizePage';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type Status = 'processing' | 'success' | 'error';

export default function AuthCallbackPage() {
  useDocumentTitle('Signing In');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokens } = useAuth();
  const [status, setStatus] = useState<Status>('processing');
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setErrorMessage(errorDescription || `${errorParam}: Login was denied or cancelled.`);
      setStatus('error');
      return;
    }

    if (!code) {
      setErrorMessage('No authorization code received. Please request a new sign-in link.');
      setStatus('error');
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;
    const codeVerifier = state
      ? sessionStorage.getItem(`${PKCE_SESSION_KEY_PREFIX}${state}`) || undefined
      : undefined;
    const loginRedirect = sessionStorage.getItem('login_redirect') || '/dashboard';

    apiClient
      .exchangeAuthCode(code, redirectUri, codeVerifier)
      .then((data) => {
        if (state) sessionStorage.removeItem(`${PKCE_SESSION_KEY_PREFIX}${state}`);
        sessionStorage.removeItem('login_redirect');
        setTokens(data.access_token, data.refresh_token);
        setStatus('success');

        // If there's a pending OAuth flow (user came from Claude/MCP),
        // resume it — the OAuthAuthorizePage will read params from sessionStorage.
        const oauthPending = sessionStorage.getItem(OAUTH_PENDING_KEY);
        if (oauthPending) {
          navigate('/oauth/authorize', { replace: true });
          return;
        }

        navigate(loginRedirect, { replace: true });
      })
      .catch((err: unknown) => {
        sessionStorage.removeItem(`${PKCE_SESSION_KEY_PREFIX}${state}`);
        const msg = err instanceof Error ? err.message : 'Authentication failed. Please try again.';
        console.error('[AuthCallback] Code exchange failed:', msg);
        setErrorMessage(msg);
        setStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
        <Card className="w-full max-w-md border-zinc-700 bg-zinc-800">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
            <CardTitle className="text-white">Signing You In</CardTitle>
            <CardDescription className="text-zinc-400">
              Please wait while we complete your authentication…
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(5,5,5)] p-4">
      <Card className="w-full max-w-md border-zinc-700 bg-zinc-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <CardTitle className="text-white">Sign-In Failed</CardTitle>
          <CardDescription className="text-zinc-400">
            {errorMessage || 'An unexpected error occurred.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Button
            onClick={() => navigate('/login')}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
